<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

export interface CardItemType {
  name?: string | null;
  value?: any;
  labelClass?: string;
  valueClass?: string;
  labelLink?: string | null;
  valueLink?: string | null;
}

export interface Props {
  item?: CardItemType,
  noBorder?: boolean,
  isLabelEllipsis?: boolean,
  isContentEllipsis?: boolean,
  noValue?: boolean,
  preIcon?: string;
  isHtml?: boolean;
  hint?: string | null;
  hintIsHtml?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  item: () => ({}),
  noBorder: false,
  isLabelEllipsis: false,
  isContentEllipsis: true,
  noValue: false,
  size: 'default',
  preIcon: '',
  isHtml: false,
  hint: null,
  hintIsHtml: false,
});

const { t } = useI18n();

const cardItemValueRef = useTemplateRef('cardItemValue');
const cardItemLabelRef = useTemplateRef('cardItemLabel');
const isTooltipLabelDisabled = ref(true);
const isTooltipValueDisabled = ref(true);

const pLabel = computed(() => {
  return props.item.name ? props.item.name.toString() : (props.item.name === undefined || props.item.name === null) ? null : t('affiliates.affiliatesInfo.not_specefied');
});

const pValue = computed(() => {
  if(props.noValue) {
    return '';
  }
  return props.item.value?.toString() && props.item.value.toString() !== '' ? props.item.value.toString() : t('affiliates.affiliatesInfo.not_specefied');
});

function labelTag(item: CardItemType) {
  return item.labelLink ? 'router-link' : 'div';
}
function valueTag(item: CardItemType) {
  return item.valueLink ? 'router-link' : 'div';
}

watch(pValue, async () => {
  await nextTick();
  if (!cardItemValueRef.value) {
    return;
  }

  isTooltipValueDisabled.value = cardItemValueRef.value.clientWidth === cardItemValueRef.value.scrollWidth;
}, { immediate: true });

watch(pLabel, async () => {
  await nextTick();

  if (!cardItemLabelRef.value) {
    return;
  }

  isTooltipLabelDisabled.value = cardItemLabelRef.value.clientWidth === cardItemLabelRef.value.scrollWidth;
}, { immediate: true });

</script>

<template>
  <div
    :class="[
      'card-item t:space-x-8',
      {'card-item_border': !noBorder},
    ]"
  >
    <slot name="title">
      <UiTooltip
        v-if="pLabel"
        :tooltip="pLabel"
        popover-class="card-item__popper"
        placement="bottom"
        :disabled="isTooltipLabelDisabled || isHtml"
      >
        <div
          ref="cardItemLabel"
          class="t:flex t:items-center t:truncate"
        >
          <UiIcon
            v-if="preIcon"
            :size="14"
            color="var(--text-primary)"
            :name="preIcon"
            class="t:mr-4 t:p-1"
          />
          <component
            :is="labelTag(props.item)"
            :class="[
              'card-item__label',
              {'card-item__label_ellipsis': isLabelEllipsis},
              {'card-item__label_empty': item && (item.name === null || item.name === '')},
              props.item.labelClass
            ]"
            v-bind="props.item.labelLink ? {
              to: props.item.labelLink,
              class: 'card-item__label_link'
            } : {}"
          >
            {{ pLabel }}
          </component>
          <UiTooltip
            v-if="hint"
            :tooltip="hint"
            :is-html="hintIsHtml"
          >
            <UiIcon
              name="question-circle"
              class="t:ml-4 t:cursor-pointer"
              color="var(--text-body-tertiary)"
              :size="14"
              substyle="fa-solid"
            />
          </UiTooltip>
        </div>
      </UiTooltip>
    </slot>
    <UiTooltip
      v-if="!noValue"
      :tooltip="pValue"
      popover-class="card-item__popper"
      placement="bottom"
      :disabled="isTooltipValueDisabled"
    >
      <slot name="value">
        <div
          ref="cardItemValue"
          class="t:truncate"
        >
          <component
            :is="valueTag(props.item)"
            :class="[
              'card-item__value',
              {'card-item__value_ellipsis': isContentEllipsis},
              {'card-item__value_empty': item && (item.value === undefined || item.value === null || item.value === '')},
              props.item.valueClass
            ]"
            v-bind="props.item.valueLink ? {
              to: props.item.valueLink,
              class: 'card-item__value_link'
            } : {}"
          >
            {{ pValue }}
          </component>
        </div>
      </slot>
    </UiTooltip>
  </div>
</template>

<style lang="scss">
.card-item{
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--text-body);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  padding: 9px 0;
  &__label{
    white-space: nowrap;
    &_ellipsis{
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    }
    &_empty {
      color: var(--text-body-tertiary);
    }
    &_link {
      color: var(--text-link);
    }
  }
  &__value{
    white-space: nowrap;
    &_ellipsis{
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    }
    &_empty {
      color: var(--text-body-tertiary);
    }
    &_link {
      color: var(--text-link);
    }
  }
  &__popper {
    max-width: 500px;
  }
  &_border{
    border-top: 1px solid var(--border-divider);
  }

}
</style>
